import type { GeneratedApp } from '@/types';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { AppDocumentView, resolveAppDocument } from '@/components/apps/AppDocumentView';
import { isJsonLike, tryParseDocumentJson } from '@/components/apps/appDocument';
import { ResearchManuscript } from '@/components/apps/ResearchManuscript';
import { sanitizeAppText } from '@/utils';
import {
  Calendar,
  ExternalLink,
  FolderOpen,
  LayoutDashboard,
  Sparkles,
  Table2,
  Tag,
  TrendingUp,
  Users,
  Share2,
} from 'lucide-react';

function isMarkdownTableRow(line: string): boolean {
  const t = line.trim();
  return t.startsWith('|') && t.endsWith('|') && t.includes('|');
}

function isMarkdownSeparatorRow(line: string): boolean {
  return /^\|?[\s\-:|]+\|?$/.test(line.trim());
}

function parseMarkdownTable(lines: string[]): string[][] | null {
  if (lines.length < 2) return null;
  if (!isMarkdownTableRow(lines[0]) || !isMarkdownSeparatorRow(lines[1])) return null;
  const rows: string[][] = [];
  for (const line of lines) {
    if (!isMarkdownTableRow(line) || isMarkdownSeparatorRow(line)) continue;
    const cells = line
      .trim()
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map((c) => c.trim());
    rows.push(cells);
  }
  return rows.length > 0 ? rows : null;
}

function splitMarkdownBlocks(text: string): Array<{ type: 'table'; rows: string[][] } | { type: 'text'; body: string }> {
  const lines = text.split('\n');
  const blocks: Array<{ type: 'table'; rows: string[][] } | { type: 'text'; body: string }> = [];
  let i = 0;
  let textBuf: string[] = [];

  const flushText = () => {
    if (textBuf.length === 0) return;
    blocks.push({ type: 'text', body: textBuf.join('\n').trim() });
    textBuf = [];
  };

  while (i < lines.length) {
    if (isMarkdownTableRow(lines[i]) && i + 1 < lines.length && isMarkdownSeparatorRow(lines[i + 1])) {
      flushText();
      const tableLines: string[] = [];
      while (i < lines.length && isMarkdownTableRow(lines[i])) {
        tableLines.push(lines[i]);
        i += 1;
      }
      const rows = parseMarkdownTable(tableLines);
      if (rows) blocks.push({ type: 'table', rows });
      continue;
    }
    textBuf.push(lines[i]);
    i += 1;
  }
  flushText();
  return blocks;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v != null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function asStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === 'string');
}

function asMatrix(v: unknown): string[][] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((row): row is unknown[] => Array.isArray(row))
    .map((row) => row.map((cell) => String(cell ?? '')));
}

function normalizeFormat(app: GeneratedApp): string {
  const data = app.app_data;
  const fromData = typeof data?.format === 'string' ? data.format : '';
  const raw = app.raw_app_type ?? app.app_type ?? '';
  const t = (fromData || raw).toLowerCase();
  if (t === 'shopping' || t === 'price_comparison' || t === 'product_comparison') return 'comparison';
  if (t === 'summary' || t === 'research') return 'research_summary';
  if (t === 'aggregation' || t === 'spreadsheet') return 'data_aggregation';
  if (t === 'collection_organization' || t === 'bookmark_collection') return 'collection';
  if (t === 'entities' || t === 'entity') return 'entity_extraction';
  return t;
}

function resolvePayload(app: GeneratedApp): Record<string, unknown> | null {
  const data = app.app_data;
  if (!data) return null;
  const nested = asRecord(data.payload);
  if (nested) return nested;
  return data;
}

export function getAppBodyContent(app: GeneratedApp): string {
  const format = normalizeFormat(app);
  if (resolveAppDocument(app) && format !== 'custom') return '';
  let contentStr = '';
  if (app.generated_code?.trim()) {
    const raw = app.generated_code.trim();
    if (isJsonLike(raw) && tryParseDocumentJson(raw)) return '';
    contentStr = raw;
  } else {
    const data = app.app_data;
    if (data && typeof data.content === 'string' && data.content.trim()) {
      contentStr = data.content.trim();
    } else if (typeof data?.summary === 'string' && data.summary.trim()) {
      contentStr = data.summary.trim();
    }
  }
  
  if (contentStr) {
    // Formatting fix: CommonMark requires double newlines to break out of paragraphs for lists and headers
    contentStr = contentStr
      .replace(/([^\n])\s*(#+ )/g, '$1\n\n$2')
      .replace(/([^\n])\s+(\d+\.\s)/g, '$1\n\n$2')
      .replace(/([^\n])\s+([*|-]\s)/g, '$1\n\n$2');
  }

  return contentStr;
}

export function isPlaceholderAppContent(content: string): boolean {
  return /^Content from \d+ tabs$/i.test(content.trim());
}

function SummaryBanner({ summary }: { summary: string }) {
  return (
    <div className="rounded-lg border border-primary-500/20 bg-gradient-to-br from-primary-500/10 to-transparent p-4">
      <div className="flex items-start gap-2">
        <Sparkles className="w-4 h-4 text-primary-300 mt-0.5 shrink-0" />
        <p className="text-sm text-khiimori-text-secondary leading-relaxed whitespace-pre-wrap">{summary}</p>
      </div>
    </div>
  );
}

function DataTable({
  columns,
  rows,
  compact,
  highlightFirstColumn,
}: {
  columns: string[];
  rows: string[][];
  compact?: boolean;
  highlightFirstColumn?: boolean;
}) {
  if (columns.length === 0 && rows.length === 0) return null;
  const header = columns.length > 0 ? columns : rows[0] ?? [];
  const body = columns.length > 0 ? rows : rows.slice(1);

  return (
    <div className="overflow-x-auto rounded-lg border border-khiimori-border/20 shadow-sm">
      <table className="min-w-full text-sm">
        <thead className="bg-khiimori-bg-elevated/40">
          <tr>
            {header.map((cell, ci) => (
              <th
                key={ci}
                className={`px-4 py-2.5 text-left font-semibold text-khiimori-text-primary border-b border-khiimori-border/20 ${
                  compact ? 'text-xs' : ''
                }`}
              >
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, ri) => (
            <tr
              key={ri}
              className={`border-b border-khiimori-border/10 last:border-0 ${
                ri % 2 === 1 ? 'bg-khiimori-bg-elevated/10' : ''
              }`}
            >
              {row.map((cell, ci) => {
                const isUrl = /^https?:\/\//i.test(cell);
                const isFirst = highlightFirstColumn && ci === 0;
                return (
                  <td
                    key={ci}
                    className={`px-4 py-2.5 align-top ${compact ? 'text-xs' : ''} ${
                      isFirst
                        ? 'font-medium text-khiimori-text-primary'
                        : 'text-khiimori-text-secondary'
                    }`}
                  >
                    {isUrl ? (
                      <a
                        href={cell}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-300 hover:text-primary-200 inline-flex items-center gap-1 break-all"
                      >
                        <span className="line-clamp-2">{cell}</span>
                        <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    ) : (
                      cell
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ComparisonView({
  payload,
  summary,
  compact,
}: {
  payload: Record<string, unknown>;
  summary?: string;
  compact?: boolean;
}) {
  const columns = asStringArray(payload.columns);
  const rows = asMatrix(payload.rows);
  const highlights = asStringArray(payload.highlights);

  return (
    <div className={compact ? 'space-y-3' : 'space-y-5'}>
      {summary && <SummaryBanner summary={summary} />}
      <div className="flex items-center gap-2 text-khiimori-text-muted">
        <Table2 className="w-4 h-4" />
        <span className="text-xs font-medium uppercase tracking-wide">Comparison</span>
      </div>
      <DataTable columns={columns} rows={rows} compact={compact} highlightFirstColumn />
      {highlights.length > 0 && (
        <ul className="space-y-1.5">
          {highlights.map((h, i) => (
            <li key={i} className="text-sm text-khiimori-text-secondary flex gap-2">
              <span className="text-primary-400">•</span>
              {h}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TimelineView({
  payload,
  summary,
  compact,
}: {
  payload: Record<string, unknown>;
  summary?: string;
  compact?: boolean;
}) {
  const events = Array.isArray(payload.events) ? payload.events : [];

  return (
    <div className={compact ? 'space-y-3' : 'space-y-5'}>
      {summary && <SummaryBanner summary={summary} />}
      <div className="flex items-center gap-2 text-khiimori-text-muted">
        <Calendar className="w-4 h-4" />
        <span className="text-xs font-medium uppercase tracking-wide">Timeline</span>
      </div>
      <div className="relative pl-6 border-l-2 border-primary-500/30 space-y-6">
        {events.map((ev, i) => {
          const e = asRecord(ev);
          if (!e) return null;
          const date = typeof e.date === 'string' ? e.date : '';
          const title = sanitizeAppText(typeof e.title === 'string' ? e.title : 'Event', 200) || 'Event';
          const description = sanitizeAppText(typeof e.description === 'string' ? e.description : '', 400);
          const url = typeof e.source_url === 'string' ? e.source_url : '';
          return (
            <div key={i} className="relative">
              <span className="absolute -left-[1.6rem] top-1 w-3 h-3 rounded-full bg-primary-500 ring-4 ring-khiimori-bg-primary" />
              {date && (
                <time className="text-xs font-medium text-primary-300 block mb-1">{date}</time>
              )}
              <h4 className={`font-semibold text-khiimori-text-primary ${compact ? 'text-sm' : ''}`}>
                {title}
              </h4>
              {description && (
                <p className="text-sm text-khiimori-text-secondary mt-1 break-words">{description}</p>
              )}
              {url && (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary-400 hover:text-primary-300 mt-1 inline-flex items-center gap-1"
                >
                  Source <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ResearchView({
  payload,
  summary,
  compact,
}: {
  payload: Record<string, unknown>;
  summary?: string;
  compact?: boolean;
}) {
  const manuscript =
    (typeof payload.manuscript_markdown === 'string' && payload.manuscript_markdown.trim()) ||
    (typeof payload.manuscriptMarkdown === 'string' && payload.manuscriptMarkdown.trim()) ||
    '';
  if (manuscript) {
    const paperId =
      (typeof payload.paper_id === 'string' && payload.paper_id) ||
      (typeof payload.paperId === 'string' && payload.paperId) ||
      null;
    const journalFormat =
      typeof payload.journal_format === 'string'
        ? payload.journal_format
        : typeof payload.journalFormat === 'string'
          ? payload.journalFormat
          : undefined;
    const usedLlm =
      typeof payload.used_llm === 'boolean'
        ? payload.used_llm
        : typeof payload.usedLlm === 'boolean'
          ? payload.usedLlm
          : undefined;
    return (
      <ResearchManuscript
        manuscript={manuscript}
        paperId={paperId}
        journalFormat={journalFormat}
        usedLlm={usedLlm}
        compact={compact}
        showDownloads={!compact}
      />
    );
  }

  const abstract = typeof payload.abstract === 'string' ? payload.abstract : summary;
  const sections = Array.isArray(payload.sections) ? payload.sections : [];
  const sources = Array.isArray(payload.sources) ? payload.sources : [];

  return (
    <div className={compact ? 'space-y-3' : 'space-y-5'}>
      {abstract && <SummaryBanner summary={abstract} />}
      {sections.map((sec, i) => {
        const s = asRecord(sec);
        if (!s) return null;
        const title = typeof s.title === 'string' ? s.title : `Section ${i + 1}`;
        const bullets = asStringArray(s.bullets);
        const body =
          (typeof s.content === 'string' && s.content.trim()) ||
          (typeof s.text === 'string' && s.text.trim()) ||
          '';
        return (
          <div
            key={i}
            className="rounded-lg border border-khiimori-border/15 bg-khiimori-bg-elevated/10 p-4"
          >
            <h4 className="text-sm font-semibold text-khiimori-text-primary mb-2">{title}</h4>
            {body ? (
              <p className="text-sm text-khiimori-text-secondary leading-relaxed whitespace-pre-wrap">{body}</p>
            ) : null}
            {bullets.length > 0 && (
              <ul className={`space-y-1.5 ${body ? 'mt-3' : ''}`}>
                {bullets.map((b, bi) => (
                  <li key={bi} className="text-sm text-khiimori-text-secondary flex gap-2">
                    <span className="text-primary-400 shrink-0">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
      {sources.length > 0 && (
        <div>
          <h4 className="text-xs font-medium uppercase tracking-wide text-khiimori-text-muted mb-2">
            Sources
          </h4>
          <div className="flex flex-wrap gap-2">
            {sources.map((src, i) => {
              const s = asRecord(src);
              const title = typeof s?.title === 'string' ? s.title : 'Source';
              const url = typeof s?.url === 'string' ? s.url : '';
              return url ? (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs px-2.5 py-1 rounded-full border border-khiimori-border/20 text-primary-300 hover:bg-primary-500/10 inline-flex items-center gap-1"
                >
                  {title}
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : null;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function CollectionView({
  payload,
  summary,
  compact,
}: {
  payload: Record<string, unknown>;
  summary?: string;
  compact?: boolean;
}) {
  const collections = Array.isArray(payload.collections) ? payload.collections : [];

  return (
    <div className={compact ? 'space-y-3' : 'space-y-5'}>
      {summary && <SummaryBanner summary={summary} />}
      <div className="flex items-center gap-2 text-khiimori-text-muted">
        <FolderOpen className="w-4 h-4" />
        <span className="text-xs font-medium uppercase tracking-wide">Collections</span>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {collections.map((col, i) => {
          const c = asRecord(col);
          if (!c) return null;
          const name = typeof c.name === 'string' ? c.name : `Collection ${i + 1}`;
          const description = typeof c.description === 'string' ? c.description : '';
          const items = Array.isArray(c.items) ? c.items : [];
          return (
            <div
              key={i}
              className="rounded-lg border border-khiimori-border/15 bg-khiimori-bg-elevated/10 p-4"
            >
              <h4 className="font-semibold text-khiimori-text-primary">{name}</h4>
              {description && (
                <p className="text-xs text-khiimori-text-muted mt-1">{description}</p>
              )}
              <ul className="mt-3 space-y-2">
                {items.map((item, ii) => {
                  const it = asRecord(item);
                  const title = typeof it?.title === 'string' ? it.title : 'Item';
                  const url = typeof it?.url === 'string' ? it.url : '';
                  const label = typeof it?.label === 'string' ? it.label : '';
                  return (
                    <li key={ii} className="text-sm">
                      {url ? (
                        <a
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-300 hover:text-primary-200 flex items-start gap-2"
                        >
                          <ExternalLink className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                          <span>
                            <span className="block">{title}</span>
                            {label && (
                              <span className="text-xs text-khiimori-text-muted">{label}</span>
                            )}
                          </span>
                        </a>
                      ) : (
                        <span className="text-khiimori-text-secondary">{title}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const entityTypeStyles: Record<string, string> = {
  product: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
  person: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  organization: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  location: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  date: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  url: 'bg-primary-500/15 text-primary-300 border-primary-500/30',
};

function EntityView({
  payload,
  summary,
  compact,
}: {
  payload: Record<string, unknown>;
  summary?: string;
  compact?: boolean;
}) {
  const entities = Array.isArray(payload.entities) ? payload.entities : [];

  return (
    <div className={compact ? 'space-y-3' : 'space-y-5'}>
      {summary && <SummaryBanner summary={summary} />}
      <div className="flex items-center gap-2 text-khiimori-text-muted">
        <Tag className="w-4 h-4" />
        <span className="text-xs font-medium uppercase tracking-wide">Extracted entities</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {entities.map((ent, i) => {
          const e = asRecord(ent);
          if (!e) return null;
          const type = typeof e.type === 'string' ? e.type : 'entity';
          const value = typeof e.value === 'string' ? e.value : '';
          const source = typeof e.source_title === 'string' ? e.source_title : '';
          const url = typeof e.url === 'string' ? e.url : '';
          const style = entityTypeStyles[type] ?? 'bg-khiimori-bg-elevated/30 text-khiimori-text-secondary border-khiimori-border/20';
          const inner = (
            <>
              <span className="text-[10px] uppercase opacity-70">{type}</span>
              <span className="font-medium">{value}</span>
              {source && !compact && (
                <span className="text-[10px] opacity-60 flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {source}
                </span>
              )}
            </>
          );
          return url ? (
            <a
              key={i}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex flex-col gap-0.5 px-3 py-2 rounded-lg border text-sm hover:opacity-90 ${style}`}
            >
              {inner}
            </a>
          ) : (
            <span
              key={i}
              className={`inline-flex flex-col gap-0.5 px-3 py-2 rounded-lg border text-sm ${style}`}
            >
              {inner}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function DashboardView({
  payload,
  summary,
  compact,
}: {
  payload: Record<string, unknown>;
  summary?: string;
  compact?: boolean;
}) {
  const widgets = Array.isArray(payload.widgets) ? payload.widgets : [];
  const sections = Array.isArray(payload.sections) ? payload.sections : [];
  const sources = Array.isArray(payload.sources) ? payload.sources : [];

  return (
    <div className={compact ? 'space-y-3' : 'space-y-5'}>
      {summary && <SummaryBanner summary={summary} />}
      {widgets.length > 0 && (
        <section className={compact ? 'space-y-2' : 'space-y-3'}>
          <div className="flex items-center gap-2 text-khiimori-text-muted">
            <LayoutDashboard className="w-4 h-4" />
            <span className="text-xs font-medium uppercase tracking-wide">Key metrics</span>
          </div>
          <div className={`grid gap-3 ${compact ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'}`}>
            {widgets.map((w, i) => {
              const widget = asRecord(w);
              if (!widget) return null;
              const title = typeof widget.title === 'string' ? widget.title : `Metric ${i + 1}`;
              const subtitle = typeof widget.subtitle === 'string' ? widget.subtitle : '';
              const value = typeof widget.value === 'string' ? widget.value : '—';
              const change = typeof widget.change === 'string' ? widget.change : '';
              const url = typeof widget.source_url === 'string' ? widget.source_url : '';
              const inner = (
                <div
                  className={`rounded-xl border border-khiimori-border/20 bg-khiimori-bg-elevated/10 p-4 h-full transition-colors ${
                    url ? 'hover:border-primary-500/30 hover:bg-primary-500/5' : ''
                  }`}
                >
                  <p className="text-xs text-khiimori-text-muted truncate">{subtitle}</p>
                  <p className={`font-semibold text-khiimori-text-primary mt-1 ${compact ? 'text-sm line-clamp-2' : 'text-base'}`}>
                    {title}
                  </p>
                  <p className={`font-bold text-primary-300 mt-2 ${compact ? 'text-lg' : 'text-2xl'}`}>{value}</p>
                  {change && (
                    <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {change}
                    </p>
                  )}
                </div>
              );
              return url ? (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block">
                  {inner}
                </a>
              ) : (
                <div key={i}>{inner}</div>
              );
            })}
          </div>
        </section>
      )}
      {sections.map((sec, i) => {
        const s = asRecord(sec);
        if (!s) return null;
        const title = typeof s.title === 'string' ? s.title : `Section ${i + 1}`;
        const bullets = asStringArray(s.bullets);
        return (
          <div
            key={i}
            className="rounded-lg border border-khiimori-border/15 bg-khiimori-bg-elevated/10 p-4"
          >
            <h4 className="text-sm font-semibold text-khiimori-text-primary mb-2">{title}</h4>
            <ul className="space-y-1.5">
              {bullets.map((b, bi) => (
                <li key={bi} className="text-sm text-khiimori-text-secondary flex gap-2">
                  <span className="text-primary-400 shrink-0">•</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      {sources.length > 0 && (
        <div>
          <h4 className="text-xs font-medium uppercase tracking-wide text-khiimori-text-muted mb-2">
            Sources
          </h4>
          <div className="flex flex-wrap gap-2">
            {sources.map((src, i) => {
              const s = asRecord(src);
              const title = typeof s?.title === 'string' ? s.title : 'Source';
              const url = typeof s?.url === 'string' ? s.url : '';
              return url ? (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs px-2.5 py-1 rounded-full border border-khiimori-border/20 text-primary-300 hover:bg-primary-500/10 inline-flex items-center gap-1"
                >
                  {title}
                  <ExternalLink className="w-3 h-3" />
                </a>
              ) : null;
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function MarkdownFallback({ content, compact }: { content: string; compact?: boolean }) {
  const blocks = splitMarkdownBlocks(content);
  return (
    <div className={compact ? 'space-y-3' : 'space-y-6'}>
      {blocks.map((block, idx) => {
        if (block.type === 'table') {
          const [header, ...body] = block.rows;
          return (
            <DataTable
              key={idx}
              columns={header}
              rows={body}
              compact={compact}
              highlightFirstColumn
            />
          );
        }
        return (
          <div
            key={idx}
            className={`prose prose-invert prose-khiimori max-w-none ${
              compact ? 'text-xs' : 'text-sm'
            }`}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>{block.body}</ReactMarkdown>
          </div>
        );
      })}
    </div>
  );
}

interface AppContentViewerProps {
  app: GeneratedApp;
  compact?: boolean;
}

export function AppContentViewer({ app, compact = false }: AppContentViewerProps) {
  const format = normalizeFormat(app);
  const data = app.app_data;
  const manuscript =
    (typeof data?.manuscript_markdown === 'string' && data.manuscript_markdown.trim()) ||
    (typeof data?.manuscriptMarkdown === 'string' && String(data.manuscriptMarkdown).trim()) ||
    '';

  // Soma research papers: prefer the full markdown manuscript over empty bullet shells.
  if ((format === 'research_paper' || format === 'research_summary') && manuscript) {
    const paperId =
      (typeof data?.paper_id === 'string' && data.paper_id) ||
      (typeof data?.paperId === 'string' && String(data.paperId)) ||
      null;
    const journalFormat =
      typeof data?.journal_format === 'string'
        ? data.journal_format
        : typeof data?.journalFormat === 'string'
          ? String(data.journalFormat)
          : undefined;
    const usedLlm =
      typeof data?.used_llm === 'boolean'
        ? data.used_llm
        : typeof data?.usedLlm === 'boolean'
          ? Boolean(data.usedLlm)
          : undefined;
    return (
      <ResearchManuscript
        manuscript={manuscript}
        paperId={paperId}
        journalFormat={journalFormat}
        usedLlm={usedLlm}
        title={app.name}
        compact={compact}
        showDownloads={!compact}
      />
    );
  }

  const payload = resolvePayload(app);
  const summary =
    (typeof data?.summary === 'string' && data.summary) ||
    (typeof data?.abstract === 'string' && data.abstract) ||
    undefined;
  const content = getAppBodyContent(app);

  const document = resolveAppDocument(app);
  if (document && format !== 'custom') {
    return <AppDocumentView document={document} compact={compact} />;
  }

  // Also prioritize iframe if format is custom and it has a preview URL or HTML content
  if (format === 'custom' && (app.preview_url || content?.trim().startsWith('<!DOCTYPE html>'))) {
    const handleShare = async () => {
      try {
        const shareUrl = app.preview_url || `${window.location.origin}/shared/${app.id}`;
        await navigator.clipboard.writeText(shareUrl);
        alert('Shareable link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy', err);
      }
    };

    return (
      <div className="flex flex-col gap-0 rounded-2xl overflow-hidden border border-primary-500/30 shadow-[0_0_40px_rgba(var(--color-primary-500),0.15)] bg-khiimori-bg-elevated/80 backdrop-blur-xl transition-all hover:shadow-[0_0_60px_rgba(var(--color-primary-500),0.25)] hover:border-primary-400/50">
        <div className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-khiimori-bg-tertiary/80 to-khiimori-bg-elevated/80 border-b border-khiimori-border/20 backdrop-blur-md">
          <div className="flex gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500/80 shadow-inner"></div>
            <div className="w-3 h-3 rounded-full bg-amber-500/80 shadow-inner"></div>
            <div className="w-3 h-3 rounded-full bg-green-500/80 shadow-inner"></div>
          </div>
          <div className="text-sm font-semibold text-khiimori-text-secondary tracking-wide">{app.name} <span className="opacity-50 font-normal">— Live Sandbox</span></div>
          <button
            onClick={handleShare}
            className="flex items-center gap-2 px-3.5 py-1.5 text-xs font-bold rounded-lg bg-gradient-to-r from-primary-600 to-indigo-600 text-white hover:from-primary-500 hover:to-indigo-500 border border-primary-500/40 transition-all shadow-lg shadow-primary-500/30 hover:shadow-primary-500/50"
          >
            <Share2 className="w-3.5 h-3.5" />
            Share App
          </button>
        </div>
        <iframe
          {...(app.preview_url ? { src: app.preview_url } : { srcDoc: content })}
          className="w-full h-[700px] border-0 bg-white"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
          title="App Preview"
        />
      </div>
    );
  }

  if (document) {
    return <AppDocumentView document={document} compact={compact} />;
  }

  const hasStructuredPayload =
    payload &&
    (asStringArray(payload.columns).length > 0 ||
      asMatrix(payload.rows).length > 0 ||
      (Array.isArray(payload.events) && payload.events.length > 0) ||
      (Array.isArray(payload.sections) && payload.sections.length > 0) ||
      (Array.isArray(payload.collections) && payload.collections.length > 0) ||
      (Array.isArray(payload.entities) && payload.entities.length > 0) ||
      (Array.isArray(payload.widgets) && payload.widgets.length > 0));

  console.log('[DEBUG] format=', format, 'hasStructuredPayload=', hasStructuredPayload, 'content length=', content?.length, 'content startsWith=', content?.trim().substring(0, 20));

  if (hasStructuredPayload) {
    switch (format) {
      case 'comparison':
        return <ComparisonView payload={payload!} summary={summary} compact={compact} />;
      case 'timeline':
        return <TimelineView payload={payload!} summary={summary} compact={compact} />;
      case 'research_summary':
      case 'research_paper':
        return <ResearchView payload={payload!} summary={summary} compact={compact} />;
      case 'data_aggregation':
        return (
          <div className={compact ? 'space-y-3' : 'space-y-5'}>
            {summary && <SummaryBanner summary={summary} />}
            <DataTable
              columns={asStringArray(payload!.columns)}
              rows={asMatrix(payload!.rows)}
              compact={compact}
            />
          </div>
        );
      case 'collection':
        return <CollectionView payload={payload!} summary={summary} compact={compact} />;
      case 'entity_extraction':
        return <EntityView payload={payload!} summary={summary} compact={compact} />;
      case 'dashboard':
        return <DashboardView payload={payload!} summary={summary} compact={compact} />;
      default:
        break;
    }
  }

  if (!content) {
    return (
      <p className="text-sm text-khiimori-text-muted">
        No generated output yet. Regenerate this app from Tab Monitor with your tabs selected.
      </p>
    );
  }

  if (format === 'custom' && content.trim().startsWith('<!DOCTYPE html>')) {
    return (
      <iframe
        srcDoc={content}
        className="w-full h-[600px] border-0 rounded-lg bg-white"
        sandbox="allow-scripts allow-same-origin allow-forms"
        title="App Preview"
      />
    );
  }

  if (isPlaceholderAppContent(content)) {
    return (
      <p className="text-sm text-khiimori-text-muted">
        This app was saved before structured output was available. Regenerate from Tab Monitor to
        get a formatted {format || 'result'}.
      </p>
    );
  }

  return <MarkdownFallback content={content} compact={compact} />;
}
